import { PointOfInterest, usePointOfInterestsQuery } from '@/__generated__/graphql';
import * as analysisState from '@/components/Analysis/state';
import { TablePagination } from '@/components/shared/TablePagination';
import { defaultPaginationSize, ALL_POIS_TAB_LABEL } from '@/constants';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { Box, Stack } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { areEqual, VariableSizeList as List, ListChildComponentProps } from 'react-window';
import { Container } from './styles';
import { feedbackContainerStyles, pointOfInterestTabsStyles } from '../styles';
import { SummaryCard, SummaryCardSkeleton } from './SummaryCard';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { upperSnakeCase } from '@/utils';
import { useMappedFilters } from '@/hooks/useMappedFilters';
import { FilterAndSearchPanel } from '@/components/shared/FilterAndSearchPanel';
import { SelectedValues } from '@/components/shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { useDebounce } from '@/hooks/useDebounce';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { EmptyStateIcon } from '@/components/shared/EmptyStateIcon';
import { poiNavigationActionsState } from './state';
import { useLiveSyncAssemblyPois } from '@/components/Analysis/shared/hooks/useLiveSyncAssemblyPois';

export const PointsOfInterest = () => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [pointOfInterests, setPointOfInterests] = useState<PointOfInterest[]>([]);
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemSize, setItemSize] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPointsOfInterest, setTotalPointsOfInterest] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const structureId = useGetStructureId();
  const selectedAssemblyId = useAtomValue(analysisState.selectedAssemblyId);
  const setPoiNavigationActions = useSetAtom(poiNavigationActionsState);
  const allPointOfInterestTemplates = useAtomValue(analysisState.allPointOfInterestTemplates);
  const [selectedFilterValues, setSelectedFilterValues] = useState<SelectedValues>({});
  const [paintRegionIds, setPaintRegionIds] = useState<string[]>([]);
  const [workpackIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 700);
  useLiveSyncAssemblyPois();

  const {
    selectedPoiFilters,
    isGoverningFilters,
    isVerifiedFilters,
    accessibilityFilters,
    statusFilters,
    isManuallyCreatedFilters,
    unAssignedPoiFilters,
    mediaAttachmentFilters,
  } = useMappedFilters(selectedFilterValues);

  const allTemplatesWithAllTab = useMemo(
    () => [{ name: ALL_POIS_TAB_LABEL, type: '', fields: [] }, ...allPointOfInterestTemplates],
    [allPointOfInterestTemplates]
  );

  const selectedTemplate = allTemplatesWithAllTab[selectedTab];
  const selectedType =
    selectedTemplate?.name === ALL_POIS_TAB_LABEL
      ? undefined
      : upperSnakeCase(selectedTemplate?.name);

  const { data: queryResponse, loading } = usePointOfInterestsQuery({
    variables: {
      input: {
        structureId,
        assemblyIds: [selectedAssemblyId ?? ''],
        type: selectedType,
        paintRegionIds,
        workpackIds,
        selectedPoiFilters,
        isVerified: isVerifiedFilters,
        accessibility: accessibilityFilters,
        status: statusFilters,
        isManuallyCreated: isManuallyCreatedFilters,
        unAssignedPoi: unAssignedPoiFilters,
        mediaAttachment: mediaAttachmentFilters,
        searchTerm: debouncedSearchTerm,
        isGoverning: isGoverningFilters,
      },
      pageInfo: {
        limit: rowsPerPage,
        skip: page * rowsPerPage,
      },
    },
  });

  const tabs = useMemo(
    () =>
      allTemplatesWithAllTab.map((template, idx) => {
        return {
          index: idx,
          label: `${template.name}`,
          component: null, // dummy, content rendered below
        };
      }),
    [allTemplatesWithAllTab, loading]
  );

  const feedbackCircleText = useMemo(() => {
    return tabs[selectedTab].label === ALL_POIS_TAB_LABEL
      ? 'No point of interest is available to show'
      : `No ${tabs[selectedTab].label.toLowerCase()} is available to show`;
  }, [selectedTab, tabs]);

  const handleTabChange = useCallback((newTab: number) => {
    setSelectedTab(newTab);
    setPage(0);
    setRowsPerPage(5);
  }, []);

  const handleSelectedFilterValuesChange = useCallback(
    (selectedValues: SelectedValues) => {
      const filteredSelectedValues = Object.fromEntries(
        Object.entries(selectedValues).filter(([, values]) => values.length > 0)
      );
      setSelectedFilterValues(filteredSelectedValues);
    },
    [setSelectedFilterValues]
  );

  const fieldsWithFilter = useMemo(() => {
    return selectedTemplate?.fields.filter(
      (field) => field.options?.length > 0 && field.displayInQuery
    );
  }, [selectedTemplate]);

  useEffect(() => {
    const data = queryResponse?.pointOfInterests;
    if (data) {
      setPointOfInterests(data as PointOfInterest[]);
      setPoiNavigationActions({ type: 'setPois', payload: data as PointOfInterest[] });
      setTotalPointsOfInterest(queryResponse?.pointOfInterestsCount?.count ?? 0);
    }
  }, [structureId, selectedAssemblyId, rowsPerPage, page, queryResponse, setPoiNavigationActions]);

  useEffect(() => {
    const currentContainer = containerRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (currentContainer?.offsetHeight) setContainerHeight(currentContainer.offsetHeight);
      if (currentContainer?.offsetWidth) setContainerWidth(currentContainer.offsetWidth);
      const rootFontSize = getComputedStyle(document.documentElement);
      const parsed = parseFloat(rootFontSize.fontSize);
      setItemSize(20.8 * parsed);
    });

    if (currentContainer) resizeObserver.observe(currentContainer);

    return () => {
      if (currentContainer) resizeObserver.unobserve(currentContainer);
    };
  }, [containerRef]);

  const listHeight = useMemo(() => {
    if (!containerHeight) return 0;
    const actualHeight = itemSize * pointOfInterests.length;
    return Math.min(actualHeight, containerHeight - 60);
  }, [containerHeight, itemSize, pointOfInterests.length]);

  const Row = memo(
    ({ index, style }: ListChildComponentProps) => (
      <div style={style}>
        <SummaryCard data={pointOfInterests[index]} poiIndex={index} />
      </div>
    ),
    areEqual
  );

  const handleChangePage = (newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number): void => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const skeletonHeight = useMemo(() => {
    if (!containerHeight) return 0;
    const actualHeight = itemSize * 3; // 3 skeletons
    return Math.max(actualHeight + 16, listHeight);
  }, [containerHeight, itemSize, listHeight]);

  const renderTabContent = () => (
    <Container position="relative" ref={containerRef} height="100%">
      {loading ? (
        <Stack height={skeletonHeight} gap="1.6rem">
          {Array.from({ length: 3 }, (_, index) => (
            <SummaryCardSkeleton key={index} />
          ))}
        </Stack>
      ) : pointOfInterests.length > 0 ? (
        <>
          <List
            key={`${containerHeight}-${containerWidth}-${pointOfInterests.length}-${selectedTab}`}
            height={listHeight}
            itemCount={pointOfInterests.length}
            width="100%"
            itemSize={() => itemSize}
            className="custom-scrollbar"
          >
            {Row}
          </List>
          <Stack>
            <TablePagination
              count={totalPointsOfInterest}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[
                { label: '5', value: 5 },
                ...defaultPaginationSize,
                { label: 'All', value: totalPointsOfInterest },
              ]}
            />
          </Stack>
        </>
      ) : (
        <Box sx={feedbackContainerStyles} data-testid="feedback-circle">
          <FeedbackCircle
            text={feedbackCircleText}
            icon={<EmptyStateIcon icon={tabs[selectedTab].label} />}
          />
        </Box>
      )}
    </Container>
  );
  return (
    <Container padding="1.4rem" height="100%">
      <BasicTabs
        tabs={tabs.map((tab) => ({
          ...tab,
          component: (
            <Container
              position="relative"
              paddingTop="1.6rem"
              sx={{
                scrollBehavior: 'smooth',
              }}
            >
              <FilterAndSearchPanel
                templateFields={fieldsWithFilter}
                selectedValues={selectedFilterValues}
                setSelectedValues={setSelectedFilterValues}
                handleSelectedChange={handleSelectedFilterValuesChange}
                showPaintRegions
                setPaintRegionIds={setPaintRegionIds}
                searchBar
                searchTerm={searchTerm}
                setSearch={setSearchTerm}
              />
              {renderTabContent()}
            </Container>
          ),
        }))}
        selectedTab={selectedTab}
        changeTab={handleTabChange}
        variant="pills"
        tabsStyles={pointOfInterestTabsStyles}
      />
    </Container>
  );
};
